import { PDFDocument, StandardFonts, rgb } from 'pdf-lib';
import { useEffect, useState } from 'react';

import PDFViewer from 'components/v2/common/pdfViewer';

import type { ClientStampedSigning } from 'types';

interface ContractProps {
    /** PDF to display: a Blob, or a base64 / data-URL string. */
    file: Blob | string | null;
    /** Signature PNG data URL drawn by the client. */
    signature: string | null;
    /**
     * Where to stamp the signature and date (`summary.signing` of a
     * `client_stamped` retainer). `null` disables stamping — the backend
     * embeds the signature itself for `server_embedded` retainers.
     */
    signaturePosition: ClientStampedSigning | null;
    onSignedAgreement: (value: Blob) => void;
    /** Called when client-side stamping fails, so the signer UI can leave its waiting state. */
    onStampError?: () => void;
}

const MONTHS = [
    'January',
    'February',
    'March',
    'April',
    'May',
    'June',
    'July',
    'August',
    'September',
    'October',
    'November',
    'December',
];

const getCurrentDate = (): string => {
    const date = new Date();
    const day = date.getDate();
    const formattedDay = day < 10 ? `0${day}` : day;

    return `${MONTHS[date.getMonth()]} ${formattedDay}, ${date.getFullYear()}`;
};

const stampAgreement = async (
    file: Blob | string,
    signature: string,
    position: ClientStampedSigning
): Promise<Blob | null> => {
    const pdfDoc = await PDFDocument.load(file instanceof Blob ? await file.arrayBuffer() : file);
    const page = pdfDoc.getPages()[position.page - 1];
    if (!page) return null;

    const imageBytes = Uint8Array.from(atob(signature.split(',')[1]), (c) => c.charCodeAt(0));
    const image = await pdfDoc.embedPng(imageBytes);
    const timesFont = await pdfDoc.embedFont(StandardFonts.TimesRoman);

    page.drawImage(image, {
        x: position.signature_x,
        y: position.signature_y,
        width: 80,
        height: 40,
    });
    page.drawText(getCurrentDate(), {
        x: position.date_x,
        y: position.date_y,
        size: 12,
        font: timesFont,
        color: rgb(0, 0, 0),
    });

    const modifiedPdfBytes = await pdfDoc.save();

    return new Blob([modifiedPdfBytes as BlobPart], { type: 'application/pdf' });
};

const Contract = ({
    file,
    signature,
    signaturePosition,
    onSignedAgreement,
    onStampError,
}: ContractProps) => {
    const [signedAgreement, setSignedAgreement] = useState<Blob | null>(null);

    useEffect(() => {
        if (!file || !signature || !signaturePosition) {
            setSignedAgreement(null);
            return;
        }

        let cancelled = false;
        stampAgreement(file, signature, signaturePosition)
            .then((blob) => {
                if (cancelled) return;
                if (!blob) {
                    onStampError?.();
                    return;
                }
                setSignedAgreement(blob);
                onSignedAgreement(blob);
            })
            .catch((error) => {
                console.error('Failed to stamp agreement:', error);
                if (!cancelled) onStampError?.();
            });

        return () => {
            cancelled = true;
        };
        // `onSignedAgreement` is intentionally not a dependency: callers pass inline
        // handlers, and re-stamping on every parent render would loop.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [file, signature, signaturePosition]);

    const displayedFile = signedAgreement ?? file;

    return displayedFile ? <PDFViewer file={displayedFile} /> : null;
};

export default Contract;
